import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getContracts } from "../services/contractService";
import {
  buildReviewTemplatePageStateFromContract,
  getDraftFileBase64,
} from "../services/generatedContractService";
import type { GeneratedContractDraft } from "../services/generatedContractService";
import {
  assertHostDocumentEditable,
  openDraftInHost,
} from "../services/hostDocumentService";
import {
  getDocumentChangeTrackingMode,
  isTrackChangesModeEnabled,
} from "../services/officeService";
import type { Contract, ContractListPagination } from "../types/contract";
import type { ContractReviewForm, Template } from "../types/template";
import { SelectContractView } from "../components/organisms/SelectContractView";

const FETCH_DEBOUNCE_MS = 250;
const TRACK_CHANGES_OPEN_BLOCK_MESSAGE =
  "Turn off Track Changes in Word (Review tab) before opening a contract from the list.";

type LoadContractsOptions = {
  readonly showSpinner: boolean;
  readonly manualRefresh?: boolean;
};

const buildSendForReviewStateFromGeneratedContract = (
  contract: Contract,
): {
  readonly template: Template;
  readonly contractReviewForm: ContractReviewForm;
  readonly generatedContractId: string;
  readonly fromContractList: true;
  readonly reviewBackState: {
    readonly draft: GeneratedContractDraft;
    readonly capturedDocumentName: string;
  };
} | null => {
  const sourceTemplateId = contract.sourceTemplateId?.trim();
  const storedFileId = contract.storedFileId?.trim();
  if (!sourceTemplateId || !storedFileId) {
    return null;
  }
  const capturedDocumentName = contract.title.trim() || "Untitled";

  return {
    template: {
      id: sourceTemplateId,
      name: capturedDocumentName,
      templateType: contract.type === "—" ? "" : contract.type,
      businessUnits: contract.businessUnit ? [contract.businessUnit] : [],
      createdByName: null,
      createdAt: null,
    },
    contractReviewForm: {
      contractName: capturedDocumentName,
      templateId: sourceTemplateId,
      contractType: contract.type === "—" ? "" : contract.type,
      businessUnit: contract.businessUnit ?? "",
      contractOrigination: contract.origination ?? "",
      effectiveDate: "",
      legalEntityId: null,
      legalEntityName: "",
      contactName: "",
      contactTitle: "",
      representativeId: null,
      representativeName: "",
      representativeTitle: "",
      representativeCompanyName: "",
      representativeAddress: "",
      representativeStreetAddress: "",
      representativeCityStateZip: "",
      representativePhone: "",
      representativeEmail: "",
      planGroupName: "",
      addressMerged: "",
      companyStreetAddress: "",
      companyCityStateZip: "",
      phone: "",
      email: "",
    },
    generatedContractId: contract.id,
    fromContractList: true,
    reviewBackState: {
      draft: {
        generatedContractId: contract.id,
        storedFileId,
        sourceTemplateId,
        documentWebUrl: "",
        wordDesktopOpenUrl: "",
      },
      capturedDocumentName,
    },
  };
};

export const ContractListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const refreshInFlightRef = useRef(false);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [search, setSearch] = useState("");
  const [dateRangeDays, setDateRangeDays] = useState<10 | 15 | 30 | undefined>(
    undefined,
  );
  const [page, setPage] = useState(1);
  const [reloadSeq, setReloadSeq] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [openDocumentError, setOpenDocumentError] = useState<string | null>(
    null,
  );
  const [openingContractId, setOpeningContractId] = useState<string | null>(
    null,
  );

  const resolveBanner = useMemo(() => {
    const st =
      (location.state as {
        resolveBanner?: { tone: "info" | "error"; message: string };
      } | null) ?? null;
    return st?.resolveBanner ?? null;
  }, [location.state]);
  const [pagination, setPagination] = useState<ContractListPagination>({
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 1,
  });

  const loadContracts = useCallback(
    async (options: LoadContractsOptions) => {
      if (options.showSpinner) {
        setIsLoading(true);
        setErrorMessage(null);
        setRefreshError(null);
      }
      try {
        const result = await getContracts({
          page,
          pageSize: pagination.pageSize,
          search,
          dateRangeDays,
          sortBy: "created_at",
          sortOrder: "desc",
        });
        setContracts(result.items);
        setPagination(result.pagination);
        if (options.manualRefresh) {
          setRefreshError(null);
        }
        if (options.showSpinner) {
          setErrorMessage(null);
        }
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Unable to load contracts";
        if (options.showSpinner) {
          setErrorMessage(msg);
          setContracts([]);
        } else if (options.manualRefresh) {
          setRefreshError(msg);
        }
      } finally {
        if (options.showSpinner) {
          setIsLoading(false);
        }
      }
    },
    [dateRangeDays, page, pagination.pageSize, search],
  );

  useEffect(() => {
    const timer = globalThis.setTimeout(() => {
      void loadContracts({ showSpinner: true });
    }, FETCH_DEBOUNCE_MS);
    return () => globalThis.clearTimeout(timer);
  }, [
    dateRangeDays,
    page,
    pagination.pageSize,
    reloadSeq,
    search,
    loadContracts,
  ]);

  const handleRefreshList = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return;
    }
    refreshInFlightRef.current = true;
    setIsRefreshing(true);
    setRefreshError(null);
    try {
      await loadContracts({ showSpinner: false, manualRefresh: true });
    } finally {
      refreshInFlightRef.current = false;
      setIsRefreshing(false);
    }
  }, [loadContracts]);

  const handleContractTitleClick = async (contract: Contract) => {
    setOpenDocumentError(null);
    if (contract.status === "generated") {
      const sendState = buildSendForReviewStateFromGeneratedContract(contract);
      if (!sendState) {
        setOpenDocumentError(
          "This generated contract cannot be sent for review right now. Try again later.",
        );
        return;
      }
      navigate(`/contractiq/templates/${sendState.template.id}/send`, {
        state: sendState,
      });
      return;
    }
    if (
      contract.status === "processing" ||
      contract.status === "ai_in_progress"
    ) {
      setOpenDocumentError(
        "This contract is still processing. Please wait for sometime.",
      );
      return;
    }
    if (contract.status === "processing_failed") {
      setOpenDocumentError(
        "Document processing failed. Please re-upload or try again.",
      );
      return;
    }
    const sid = contract.storedFileId?.trim();
    if (!sid) {
      setOpenDocumentError(
        "This contract cannot be opened right now. Try again later.",
      );
      return;
    }
    setOpeningContractId(contract.id);
    try {
      await assertHostDocumentEditable();
      const trackMode = await getDocumentChangeTrackingMode();
      if (!trackMode.ok) {
        setOpenDocumentError(
          `Could not read Track Changes mode in Word. ${trackMode.message}`.slice(
            0,
            300,
          ),
        );
        return;
      }
      if (isTrackChangesModeEnabled(trackMode.mode)) {
        setOpenDocumentError(TRACK_CHANGES_OPEN_BLOCK_MESSAGE);
        return;
      }
      const base64 = await getDraftFileBase64(sid);
      await openDraftInHost(base64);
      const docReadyState = { hostDocumentOpenedFromList: true as const };
      // Completed: open host file then read-only review summary (FR-004).
      if (contract.status === "Completed") {
        navigate(`/contractiq/contracts/${contract.id}/review`, {
          state: docReadyState,
        });
        return;
      }
      const templateState = buildReviewTemplatePageStateFromContract(contract);
      if (templateState) {
        navigate(
          `/contractiq/templates/${templateState.draft.sourceTemplateId}/review`,
          {
            state: {
              draft: templateState.draft,
              capturedDocumentName: templateState.capturedDocumentName,
              ...docReadyState,
            },
          },
        );
      } else {
        navigate(`/contractiq/contracts/${contract.id}/review`, {
          state: docReadyState,
        });
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : "Unable to open this contract. Try again later.";
      setOpenDocumentError(msg);
    } finally {
      setOpeningContractId(null);
    }
  };

  const handleGoToReview = async (contract: Contract) => {
    setOpenDocumentError(null);
    if (
      contract.status === "processing" ||
      contract.status === "ai_in_progress"
    ) {
      setOpenDocumentError(
        "This contract is still processing. Please wait for sometime.",
      );
      return;
    }
    if (contract.status === "processing_failed") {
      setOpenDocumentError(
        "Document processing failed. Please re-upload or try again.",
      );
      return;
    }
    const sid = contract.storedFileId?.trim();
    if (!sid) {
      setOpenDocumentError(
        "This contract cannot be opened right now. Try again later.",
      );
      return;
    }
    setOpeningContractId(contract.id);
    try {
      await assertHostDocumentEditable();
      const trackMode = await getDocumentChangeTrackingMode();
      if (!trackMode.ok) {
        setOpenDocumentError(
          `Could not read Track Changes mode in Word. ${trackMode.message}`.slice(
            0,
            300,
          ),
        );
        return;
      }
      if (isTrackChangesModeEnabled(trackMode.mode)) {
        setOpenDocumentError(TRACK_CHANGES_OPEN_BLOCK_MESSAGE);
        return;
      }
      const base64 = await getDraftFileBase64(sid);
      await openDraftInHost(base64);
      const docReadyState = { hostDocumentOpenedFromList: true as const };
      if (contract.status === "Completed") {
        navigate(`/contractiq/contracts/${contract.id}/review`, {
          state: docReadyState,
        });
        return;
      }
      const templateState = buildReviewTemplatePageStateFromContract(contract);
      if (templateState) {
        navigate(
          `/contractiq/templates/${templateState.draft.sourceTemplateId}/review`,
          {
            state: {
              draft: templateState.draft,
              capturedDocumentName: templateState.capturedDocumentName,
              ...docReadyState,
            },
          },
        );
      } else {
        navigate(`/contractiq/contracts/${contract.id}/review`, {
          state: docReadyState,
        });
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : "Unable to open this contract. Try again later.";
      setOpenDocumentError(msg);
    } finally {
      setOpeningContractId(null);
    }
  };

  return (
    <SelectContractView
      contracts={contracts}
      pagination={pagination}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      resolveBanner={resolveBanner}
      errorMessage={errorMessage}
      refreshError={refreshError}
      onDismissRefreshError={() => {
        setRefreshError(null);
      }}
      openDocumentError={openDocumentError}
      openingContractId={openingContractId}
      searchTerm={search}
      selectedDateRangeDays={dateRangeDays}
      onContractTitleClick={handleContractTitleClick}
      onGoToReview={handleGoToReview}
      onSearchChange={(value) => {
        setPage(1);
        setSearch(value);
      }}
      onDateRangeChange={(value) => {
        setPage(1);
        setDateRangeDays(value);
      }}
      onPageChange={setPage}
      onRefresh={handleRefreshList}
      onRetry={() => {
        setReloadSeq((s) => s + 1);
      }}
    />
  );
};
